import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, RefreshCw, Paperclip, X as XIcon, MessagesSquare, Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 3 * 1024 * 1024; // 3 MB
const ACCEPT = "image/png,image/jpeg,image/webp";

// Stage-abhängige Begrüßung + Impulse. Die Welcome-Nachricht wird als erste
// Assistent-Message eingefügt, damit Einsteiger:innen nicht mit einem leeren
// Chat konfrontiert werden — der Coach nimmt sie direkt an die Hand.
const WELCOME_BY_STAGE = {
  orientierung:
    "Willkommen bei TradeMind. Schön, dass du da bist.\n\n" +
    "Bevor wir starten, eine kurze Frage: Wo stehst du gerade?\n\n" +
    "• Sind dir Begriffe wie Stop-Loss, Hebel oder Long/Short schon einmal begegnet?\n" +
    "• Hast du bereits investiert — in Aktien, Krypto oder ETFs?\n" +
    "• Oder fängst du wirklich ganz von vorne an?\n\n" +
    "Wir gehen den nächsten Schritt gemeinsam — ohne Fachjargon und in deinem Tempo. Was passt am besten zu dir?",
  struktur:
    "Willkommen zurück. Bevor wir in die Details gehen: Beschreib mir kurz, wie du heute an einen Trade herangehst. Wo läuft es rund, und wo bist du dir noch unsicher?",
  optimierung:
    "Hallo. Woran wollen wir heute arbeiten — an der Setup-Qualität, am Risiko-Management oder an einem wiederkehrenden Fehler in deinem R-Verlauf?",
};

const SUGGESTIONS_BY_STAGE = {
  orientierung: [
    "Was heißt «Long» und «Short» eigentlich genau?",
    "Wofür brauche ich einen Stop-Loss?",
    "Was ist der Unterschied zwischen Spot und Futures?",
    "Wie groß sollte meine erste Position sein?",
  ],
  struktur: [
    "Wie erkenne ich Muster in meinen bisherigen Trades?",
    "Was gehört alles in ein sauberes Regelwerk?",
    "Wie strukturiere ich meine Reflexion nach jedem Trade?",
    "Woran erkenne ich, dass eine Strategie zu mir passt?",
  ],
  optimierung: [
    "Wie halte ich meine Regelkonformität diese Woche stabil?",
    "Was sagt mein R-Verlauf über meine Konsistenz aus?",
    "Wie gehe ich mit einem Regelverstoß um?",
    "Welche Frage sollte ich mir nach jedem Trade stellen?",
  ],
};

const DEFAULT_WELCOME =
  "Hallo. Womit sollen wir beginnen? Du kannst mich zu einem konkreten Trade fragen, ein Muster besprechen oder eine Reflexionsfrage stellen — auch ein Chart-Screenshot ist willkommen, den wir gemeinsam durchgehen.";
const DEFAULT_SUGGESTIONS = SUGGESTIONS_BY_STAGE.optimierung;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function KiCoach() {
  const { user } = useAuth();
  const stage = user?.onboarding?.stage || null;
  const welcome = useMemo(
    () => WELCOME_BY_STAGE[stage] || DEFAULT_WELCOME,
    [stage],
  );
  const suggestions = useMemo(
    () => SUGGESTIONS_BY_STAGE[stage] || DEFAULT_SUGGESTIONS,
    [stage],
  );
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: welcome }]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);   // {id, name, size, type, data_url}
  const [loading, setLoading] = useState(false);
  const [lastFailed, setLastFailed] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [tradeContext, setTradeContext] = useState(null); // { trade_number, asset, is_open }
  const sessionId = useRef(`sess_${Date.now()}`);
  const scrollerRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const r = await axios.get(`${API}/coach/sessions`, { withCredentials: true });
      setSessions(r.data?.sessions || []);
    } catch { /* still */ }
    finally { setLoadingSessions(false); }
  };
  useEffect(() => { loadSessions(); }, []);

  // Trade-Kontext aus dem Trading Journal: PositionDetailDialog legt einen
  // Entwurf im sessionStorage ab, bevor er hierher navigiert. Wir starten
  // dann einen neuen Chat, füllen die Eingabe vor und markieren die
  // Session so, dass der User sieht, worüber wir gerade reden.
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem("coach_trade_context"); } catch { /* still */ }
    if (!raw) return;
    try { sessionStorage.removeItem("coach_trade_context"); } catch { /* still */ }
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    if (!payload || !payload.draft) return;
    sessionId.current = `sess_${Date.now()}`;
    setMessages([{ role: "assistant", content: welcome }]);
    setInput(payload.draft);
    setTradeContext({
      trade_number: payload.trade_number,
      asset: payload.asset,
      is_open: !!payload.is_open,
    });
    setAttachments([]);
    setLastFailed(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSession = async (sid) => {
    if (loading) return;
    try {
      const r = await axios.get(`${API}/coach/sessions/${sid}`, { withCredentials: true });
      const msgs = (r.data?.messages || []).map((m) => ({ role: m.role, content: m.content }));
      if (!msgs.length) return;
      sessionId.current = sid;
      setMessages(msgs);
      setInput("");
      setAttachments([]);
      setLastFailed(null);
    } catch {
      toast.error("Chat konnte nicht geladen werden");
    }
  };

  const newChat = () => {
    if (loading) return;
    sessionId.current = `sess_${Date.now()}`;
    setMessages([{ role: "assistant", content: welcome }]);
    setInput("");
    setAttachments([]);
    setLastFailed(null);
    setTradeContext(null);
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Diesen Chat aus der Chronik entfernen?")) return;
    try {
      await axios.delete(`${API}/coach/sessions/${sid}`, { withCredentials: true });
      setSessions((prev) => prev.filter((s) => s.session_id !== sid));
      if (sid === sessionId.current) newChat();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  // Wenn `user` (und damit die stage) erst nach dem ersten Render eintrudelt
  // und der Chat noch unberührt ist, tauschen wir die Willkommens-Nachricht
  // gegen die stage-passende aus — ohne bereits gesendete User-Nachrichten
  // anzurühren.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === "assistant" && prev[0].content !== welcome) {
        return [{ role: "assistant", content: welcome }];
      }
      return prev;
    });
  }, [welcome]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const next = [...attachments];
    for (const f of files) {
      if (next.length >= MAX_IMAGES) {
        toast.error("Zu viele Bilder", { description: `Maximal ${MAX_IMAGES} Screenshots pro Nachricht.` });
        break;
      }
      if (!ACCEPT.split(",").includes(f.type)) {
        toast.error(`«${f.name}» wird nicht unterstützt`, { description: "Nur PNG, JPEG oder WEBP." });
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        toast.error(`«${f.name}» ist zu groß`, { description: "Maximal 3 MB pro Bild." });
        continue;
      }
      try {
        const dataUrl = await readAsDataUrl(f);
        next.push({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          name: f.name || "screenshot.png",
          size: f.size,
          type: f.type,
          data_url: dataUrl,
        });
      } catch {
        toast.error(`«${f.name}» konnte nicht gelesen werden.`);
      }
    }
    setAttachments(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) { e.preventDefault(); addFiles(files); }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    const imgs = attachments.map((a) => a.data_url);
    if ((!msg && imgs.length === 0) || loading) return;
    const userContent = msg || (imgs.length === 1 ? "(Screenshot angehängt)" : `(${imgs.length} Screenshots angehängt)`);
    const next = [...messages, { role: "user", content: userContent, images: attachments }];
    setMessages(next);
    setInput("");
    setAttachments([]);
    setLastFailed(null);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/coach/chat`,
        {
          session_id: sessionId.current,
          messages: next.map(({ role, content }) => ({ role, content })),
          images: imgs,
        },
        { withCredentials: true, timeout: 60000 }
      );
      const reply = res.data?.reply?.toString().trim();
      if (!reply) throw new Error("empty");
      setMessages([...next, { role: "assistant", content: reply }]);
      loadSessions(); // Chronik aktualisieren, damit neue Session sofort erscheint.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let hint;
      if (status === 401) hint = "Bitte melde dich neu an.";
      else if (status === 502 || status === 503) hint = detail || "Der Coach ist kurz nicht erreichbar. In wenigen Sekunden erneut versuchen.";
      else if (e?.code === "ECONNABORTED") hint = "Die Antwort dauert länger als üblich. Nochmal versuchen?";
      else hint = detail || "Bitte in einem Moment erneut versuchen.";
      toast.error("KI-Coach nicht erreichbar", { description: hint });
      setLastFailed({ text: msg, imgs });
    } finally {
      setLoading(false);
    }
  };

  const retryLast = () => {
    if (!lastFailed || loading) return;
    setLoading(true);
    setLastFailed(null);
    (async () => {
      try {
        const res = await axios.post(
          `${API}/coach/chat`,
          {
            session_id: sessionId.current,
            messages: messages.map(({ role, content }) => ({ role, content })),
            images: lastFailed.imgs || [],
          },
          { withCredentials: true, timeout: 60000 }
        );
        const reply = res.data?.reply?.toString().trim();
        if (!reply) throw new Error("empty");
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      } catch (e) {
        const hint = e?.response?.data?.detail || "Bitte in einem Moment erneut versuchen.";
        toast.error("Erneuter Versuch fehlgeschlagen", { description: hint });
        setLastFailed(lastFailed);
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div>
      <PageHeader
        eyebrow="KI-Coach"
        title="KI-Coach"
        description="Fragen zu Setups, Reflexion und Mustern in deinem Handel. Chart-Screenshots kannst du direkt anhängen."
        testid="coach-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <section className="card-surface flex flex-col overflow-hidden h-[62vh] min-h-[520px]">
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-5 md:px-8 py-6 space-y-6"
            data-testid="coach-messages"
          >
            {messages.map((m, i) => (
              <Message key={i} role={m.role} content={m.content} images={m.images} />
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Coach denkt nach…
              </div>
            )}
            {lastFailed && !loading && (
              <div className="flex items-center gap-2 text-sm text-[hsl(0_66%_72%)]" data-testid="coach-retry-row">
                Letzte Nachricht konnte nicht beantwortet werden.
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full h-8 px-3"
                  onClick={retryLast}
                  data-testid="coach-retry-btn"
                >
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Nochmal senden
                </Button>
              </div>
            )}
          </div>

          <div
            className={`border-t border-border p-4 md:p-5 transition-colors ${dragActive ? "bg-primary/10" : ""}`}
            onDragEnter={onDragOver}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-testid="coach-composer"
          >
            {tradeContext && (
              <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[hsl(43_74%_49%/0.4)] bg-[hsl(43_74%_49%/0.08)] px-3 py-2 text-xs" data-testid="coach-trade-context">
                <span className="text-[hsl(43_74%_66%)]">
                  Gespräch zu Trade <span className="font-mono">{tradeContext.trade_number || "—"}</span>
                  {tradeContext.asset ? ` · ${tradeContext.asset}` : ""}
                  {tradeContext.is_open ? " · läuft noch" : " · geschlossen"}
                </span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setTradeContext(null)}
                  data-testid="coach-trade-context-clear"
                >
                  <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            )}
            {attachments.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2" data-testid="coach-attach-list">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="relative group rounded-lg overflow-hidden border border-border bg-secondary/40"
                    data-testid={`coach-attach-${a.id}`}
                  >
                    <img src={a.data_url} alt={a.name} className="h-16 w-16 object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/90 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`«${a.name}» entfernen`}
                      data-testid={`coach-attach-remove-${a.id}`}
                    >
                      <XIcon className="h-3 w-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full h-11 w-11 shrink-0"
                aria-label="Screenshot anhängen"
                data-testid="coach-attach-btn"
                disabled={loading || attachments.length >= MAX_IMAGES}
              >
                <Paperclip className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
                data-testid="coach-attach-input"
              />
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={onPaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Frage stellen, Beobachtung teilen — oder Screenshot einfügen (Strg+V)"
                rows={2}
                className="min-h-[52px] resize-none"
                data-testid="coach-input"
              />
              <Button
                onClick={() => send()}
                disabled={loading || (!input.trim() && attachments.length === 0)}
                className="rounded-full h-11 px-5"
                data-testid="coach-send"
              >
                <Send className="mr-2 h-4 w-4" strokeWidth={1.5} /> Senden
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              Enter zum Senden, Umschalt + Enter für Zeilenumbruch · PNG/JPEG/WEBP, max. 3 MB pro Bild, max. {MAX_IMAGES} pro Nachricht.
            </div>
          </div>
        </section>

        <aside className="card-surface p-5 h-fit" data-testid="coach-sessions">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <MessagesSquare className="h-4 w-4 text-primary" strokeWidth={1.5} />
              <div className="overline">Letzte Gespräche</div>
            </div>
            <button
              type="button"
              onClick={newChat}
              disabled={loading}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
              data-testid="coach-new-chat"
              title="Neuer Chat"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.75} /> Neu
            </button>
          </div>
          {loadingSessions && sessions.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 text-center">Wird geladen …</div>
          )}
          {!loadingSessions && sessions.length === 0 && (
            <div className="text-xs text-muted-foreground py-4 leading-relaxed">
              Noch keine Gespräche. Sobald du eine Frage stellst, erscheint sie hier — jederzeit wieder aufrufbar.
            </div>
          )}
          {sessions.length > 0 && (
            <ul className="space-y-1.5">
              {sessions.map((s) => {
                const isActive = s.session_id === sessionId.current;
                return (
                  <li key={s.session_id}>
                    <button
                      onClick={() => openSession(s.session_id)}
                      disabled={loading}
                      className={`group text-left w-full rounded-lg border p-2.5 transition-colors disabled:opacity-50 ${
                        isActive
                          ? "border-primary/50 bg-primary/10"
                          : "border-border hover:bg-secondary"
                      }`}
                      data-testid={`coach-session-${s.session_id}`}
                      title={s.title}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate leading-snug">
                            {s.title || "Chat"}
                          </div>
                          <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" strokeWidth={1.5} />
                            <RelTime iso={s.updated_at} />
                            <span>·</span>
                            <span>{s.message_count} Nachrichten</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => deleteSession(s.session_id, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-[hsl(0_66%_72%)] shrink-0 p-0.5 -mr-0.5"
                          aria-label="Chat löschen"
                          data-testid={`coach-session-del-${s.session_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

function RelTime({ iso }) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  let label;
  if (min < 1) label = "gerade eben";
  else if (min < 60) label = `vor ${min} min`;
  else if (min < 24 * 60) label = `vor ${Math.floor(min / 60)} h`;
  else if (min < 7 * 24 * 60) label = `vor ${Math.floor(min / (24 * 60))} T`;
  else label = new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return <span>{label}</span>;
}

function Message({ role, content, images }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-secondary/70 text-foreground border border-border"
        }`}
      >
        {images && images.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {images.map((a) => (
              <img
                key={a.id}
                src={a.data_url}
                alt={a.name}
                className="max-h-48 rounded-md border border-border/40"
              />
            ))}
          </div>
        )}
        {isUser ? (
          <div style={{ whiteSpace: "pre-wrap" }}>{content}</div>
        ) : (
          <div className="coach-markdown">
            <ReactMarkdown
              components={{
                p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                strong: ({ node, ...props }) => <strong className="font-semibold text-foreground" {...props} />,
                em: ({ node, ...props }) => <em className="italic" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-2 space-y-1" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-2 space-y-1" {...props} />,
                li: ({ node, ...props }) => <li className="text-sm" {...props} />,
                code: ({ node, inline, ...props }) => inline
                  ? <code className="bg-secondary rounded px-1 py-0.5 text-xs font-mono" {...props} />
                  : <code className="block bg-secondary rounded p-2 text-xs font-mono overflow-x-auto" {...props} />,
                h1: ({ node, ...props }) => <h4 className="font-heading text-base mt-2 mb-1" {...props} />,
                h2: ({ node, ...props }) => <h4 className="font-heading text-base mt-2 mb-1" {...props} />,
                h3: ({ node, ...props }) => <h4 className="font-heading text-sm mt-2 mb-1" {...props} />,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
